import AnimateOnScroll from "./AnimateOnScroll";

const steps = [
  {
    step: "01",
    icon: "ri-links-line",
    title: "Get your unique referral link",
    desc: "Register as an Introducer and we'll issue you a unique URL tied to your account — ready to share straight away.",
  },
  {
    step: "02",
    icon: "ri-share-forward-2-line",
    title: "Share it with your network",
    desc: "Promote the link however works for you — email, social, events, referrals. Anyone who clicks and purchases is tracked automatically.",
  },
  {
    step: "03",
    icon: "ri-money-pound-circle-line",
    title: "Earn commission on purchases",
    desc: "Every qualifying purchase made through your link earns you 10% of the billed price. Monthly subscriptions compound as your referred users keep subscribing.",
    warning: true,
  },
];

const commissionableProducts = [
  {
    tag: "One-off sale",
    label: "Single Use",
    price: "£49.99",
    priceSuffix: "one-off",
    icon: "ri-time-line",
    desc: "24 hours of full platform access — ideal for prospecting meetings. You earn 10% of the billed price (£5.00) as a one-off commission for each purchase your link generates.",
    badge: null,
  },
  {
    tag: "Recurring",
    label: "Monthly Subscription",
    price: "£12.99",
    priceSuffix: "/month · per end user",
    icon: "ri-calendar-check-line",
    desc: "Rolling monthly access. You earn 10% of the billed price (£1.30/month per end user) — recurring for as long as the referred user keeps their subscription active.",
    badge: "Ongoing earnings",
  },
];

export default function IntroducerSection() {
  const scrollToRegister = () => {
    const el = document.querySelector("#register");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="introducer"
      className="bg-[#EFEBE5] py-24 border-t border-white"
    >
      <div className="max-w-6xl mx-auto px-6">
        {/* Header */}
        <AnimateOnScroll variant="fade-up">
          <div className="text-center mb-14">
            <p className="text-xs font-bold tracking-widest text-brown-300 uppercase mb-4">
              Introducer Programme
            </p>
            <h2 className="text-4xl lg:text-5xl font-bold text-[#1a1a2e] mb-5">
              Earn by Introducing Envizage.AI
            </h2>
            <p className="text-base text-[#6a6a7a] max-w-2xl mx-auto leading-relaxed">
              An <strong className="text-[#1a1a2e]">Introducer</strong> —
              sometimes called an affiliate — is someone who refers others to
              Envizage.AI and earns a commission on qualifying purchases. You
              get a unique link, share it with whoever you think would benefit,
              and earn every time someone buys through it. Simple.
            </p>
          </div>
        </AnimateOnScroll>

        {/* How it works — 3 steps */}
        <AnimateOnScroll variant="fade-up" delay={60}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
            {steps.map((s, i) => (
              <AnimateOnScroll key={s.step} variant="fade-up" delay={i * 100}>
                <div className="bg-white border border-[#EDE8DF] rounded-2xl p-6 flex flex-col gap-5 h-full hover:border-[#C0B8B0] transition-colors">
                  {/* Step number + icon row */}
                  <div className="flex items-center justify-between">
                    <span className="text-lg  tracking-widest text-brown-300 uppercase">
                      {s.step}
                    </span>
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-brown-300 flex-shrink-0">
                      <i className={`${s.icon} text-base text-brown-300`}></i>
                    </div>
                  </div>
                  {/* Divider */}
                  <div className="h-px bg-[#EDE8DF]" />
                  {/* Text */}
                  <div>
                    <h3 className="text-md font-normal text-[#1a1a2e] mb-2 flex items-center gap-1.5">
                      {s.title}
                      {'warning' in s && s.warning && (
                        <span className="relative group/tip cursor-pointer">
                          <i className="ri-error-warning-line text-base text-amber-600"></i>
                          <span className="pointer-events-none absolute bottom-full right-0 mb-2 w-72 rounded-xl border border-amber-200/70 bg-amber-50 p-4 shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-200 z-50">
                            <span className="block text-sm font-bold text-[#1a1a2e] mb-1">Not applicable for Co-Creator pricing</span>
                            <span className="block text-sm text-[#6a6a7a] leading-relaxed">
                              The Introducer commission <strong className="text-[#1a1a2e]">does not apply</strong> to the discounted Co-Creator founding rate (£3.25/month). Commission is only earned on the standard Single Use (£49.99) and standard Monthly Subscription (£12.99/month per end user) purchases.
                            </span>
                          </span>
                        </span>
                      )}
                    </h3>
                    <p className="text-md text-[#6a6a7a] leading-relaxed">
                      {s.desc}
                    </p>
                  </div>
                </div>
              </AnimateOnScroll>
            ))}
          </div>
        </AnimateOnScroll>

        {/* What earns commission */}
        {false && (
          <AnimateOnScroll variant="fade-up" delay={80}>
            <div className="mb-6">
              <p className="text-xs font-bold tracking-widest text-[#9a9aaa] uppercase mb-5 text-center">
                Commission applies to these products
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {commissionableProducts.map((p, i) => (
                  <AnimateOnScroll
                    key={p.label}
                    variant="fade-up"
                    delay={i * 90}
                  >
                    <div className="relative bg-white border border-[#EDE8DF] rounded-2xl p-6 flex flex-col gap-4 hover:border-[#C0B8B0] transition-colors group">
                      {p.badge && (
                        <span className="absolute top-4 right-4 text-[10px] bg-[#E8F5EE] text-[#1BB184] border border-[#B8DFD0] px-2.5 py-1 rounded-2xl whitespace-nowrap">
                          {p.badge}
                        </span>
                      )}
                      {/* Tag */}
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-[#9a9aaa] bg-white rounded-xl px-2.5 py-1 whitespace-nowrap">
                          {p.tag}
                        </span>
                      </div>
                      {/* Price */}
                      <div className="flex items-end gap-2">
                        <div className="w-10 h-10 flex items-center justify-center rounded bg-brown-300 flex-shrink-0">
                          <i
                            className={`${p.icon} text-base text-brown-300`}
                          ></i>
                        </div>
                        <div>
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-3xl  text-[#1a1a2e] leading-none">
                              {p.price}
                            </span>
                            <span className="text-xs text-[#9a9aaa]">
                              {p.priceSuffix}
                            </span>
                          </div>
                          <p className="text-xs font-semibold text-brown-300 mt-0.5">
                            {p.label}
                          </p>
                        </div>
                      </div>
                      <p className="text-md text-[#6a6a7a] leading-relaxed">
                        {p.desc}
                      </p>
                      {/* Commission tick */}
                      <div className="flex items-center gap-2 pt-2 border-t border-[#EDE8DF]">
                        <div className="w-5 h-5 flex items-center justify-center rounded bg-transparent">
                          <i className="ri-check-line text-xs text-[#027373]"></i>
                        </div>
                        <span className="text-xs font-semibold text-[#027373]">
                          Commission eligible
                        </span>
                      </div>
                    </div>
                  </AnimateOnScroll>
                ))}
              </div>
            </div>
          </AnimateOnScroll>
        )}

        {/* CTA banner */}
        <AnimateOnScroll variant="fade-up" delay={120}>
          <div className="rounded-2xl bg-[#222] px-6 sm:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-6 relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded bg-[#1BB184]/20 blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-10 -left-10 w-32 h-32 rounded bg-[#1BB184]/15 blur-3xl pointer-events-none"></div>
            <div className="relative flex items-start sm:items-center gap-4">
              <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-white/10 flex-shrink-0">
                <i className="ri-links-line text-lg text-white"></i>
              </div>
              <div>
                <p className="text-white font-bold text-md sm:text-base mb-0.5">
                  Interested in becoming an Introducer?
                </p>
                <p className="text-md text-white/55 leading-relaxed max-w-lg">
                  Register your interest below and we&apos;ll reach out with
                  your unique referral link and full commission details.
                </p>
              </div>
            </div>
            <button
              onClick={scrollToRegister}
              className="relative w-full sm:w-auto flex-shrink-0 inline-flex items-center justify-center gap-2 bg-white hover:bg-[#fcfcfc] text-black text-md font-bold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-user-add-line"></i>
              Register Your Interest
            </button>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
}
